import { SetStateAction, useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import type { Match } from '@views/WarehouseDetails/WarehouseDetails';
import {
  ACCEPT_WAREHOUSE_MATCH,
  GET_WAREHOUSE_MATCH_PRICING_SCHEDULE,
} from '@constants';
import { useAuthHttp } from '@hooks';
import { toDisplayDate } from '@utils';
import {
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
} from '../../store/jotai';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { FloxSpinner } from '../FloxSpinner';
import {
  PricingSchedule,
  PricingScheduleAcceptance,
  PricingScheduleContractDataRow,
  PricingScheduleForecastClause,
  WarehouseMatchPricingScheduleResponse,
} from './pricingScheduleTypes';

export type PricingScheduleModalProps = {
  matchId: string;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  mode: 'accept' | 'view';
  onAccepted?: (match: Match) => void;
};

type TableRow = {
  label: string;
  value: string;
  isMuted?: boolean;
};

const webAddress = /(https?:\/\/\S*[^\s.,;:!?)])/;

const whatTheBackendSaid = (error: unknown) =>
  (
    error as {
      response?: { data?: { errors?: { errorMessage?: string }[] } };
    }
  ).response?.data?.errors?.[0]?.errorMessage;

const hasTheServerAnswered = (error: unknown) =>
  Boolean((error as { response?: unknown }).response);

const acceptanceRecordOf = ({
  acceptedByName,
  acceptingBusinessName,
  acceptedAt,
}: PricingScheduleAcceptance) => {
  if (!acceptedAt) return null;
  const acceptedOn = toDisplayDate(acceptedAt);
  return acceptedByName
    ? `Accepted by ${acceptedByName} for ${acceptingBusinessName} on ${acceptedOn}`
    : `Accepted for ${acceptingBusinessName} on ${acceptedOn}`;
};

const withWebAddressesAsLinks = (text: string) =>
  text.split(webAddress).map((part) =>
    webAddress.test(part) ? (
      <a
        key={part}
        href={part}
        target='_blank'
        rel='noopener noreferrer'
        className='text-primary underline hover:text-blue-800'
      >
        {part}
      </a>
    ) : (
      part
    )
  );

function ErrorAlert({ message }: { message: string }) {
  return (
    <div
      role='alert'
      className='flex flex-none items-start gap-2.5 rounded-lg bg-red-50 px-3 py-2.5 ring-1 ring-inset ring-red-100'
    >
      <ExclamationTriangleIcon
        aria-hidden='true'
        className='mt-0.5 h-4 w-4 shrink-0 text-red-500'
      />
      <p className='text-sm text-red-700'>{message}</p>
    </div>
  );
}

function ClauseLead({ number, text }: { number: number; text: string }) {
  return (
    <p className='text-sm leading-relaxed text-gray-900'>
      <span className='font-semibold'>{number}.</span> {text}
    </p>
  );
}

function ContractData({ rows }: { rows: PricingScheduleContractDataRow[] }) {
  return (
    <dl className='divide-y divide-gray-100 rounded-xl border border-gray-200'>
      {rows.map((row) => (
        <div
          key={row.label}
          className='grid grid-cols-1 gap-1 px-4 py-2.5 sm:grid-cols-5 sm:gap-4'
        >
          <dt className='text-sm text-gray-500 sm:col-span-2'>{row.label}</dt>
          <dd className='break-words text-sm text-gray-900 sm:col-span-3'>
            {row.url ? (
              <a
                href={row.url}
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary underline hover:text-blue-800'
              >
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TwoColumnTable({
  labelTitle,
  valueTitle,
  rows,
}: {
  labelTitle: string;
  valueTitle: string;
  rows: TableRow[];
}) {
  return (
    <div className='overflow-hidden rounded-xl border border-gray-200'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            <th
              scope='col'
              className='px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'
            >
              {labelTitle}
            </th>
            <th
              scope='col'
              className='px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500'
            >
              {valueTitle}
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-100 bg-white'>
          {rows.map((row) => (
            <tr
              key={row.label}
              className={row.isMuted ? 'text-gray-400' : 'text-gray-900'}
            >
              <td className='px-4 py-2.5 text-sm'>{row.label}</td>
              <td
                className={`px-4 py-2.5 text-right text-sm ${
                  row.isMuted ? 'italic' : 'font-medium'
                }`}
              >
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Forecast({ forecast }: { forecast: PricingScheduleForecastClause }) {
  return (
    <section className='rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4'>
      <div className='flex items-start justify-between gap-3'>
        <ClauseLead number={forecast.number} text={forecast.heading} />
        <span className='shrink-0 rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-700'>
          Indicative
        </span>
      </div>
      <ol className='mt-3 flex flex-col gap-1.5'>
        {forecast.items.map((item) => (
          <li key={item.letter} className='flex gap-2 text-sm text-gray-700'>
            <span className='font-medium text-gray-900'>{item.letter}.</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function PricingScheduleBody({
  pricingSchedule,
}: {
  pricingSchedule: PricingSchedule;
}) {
  const { rates, volumeDiscounts } = pricingSchedule;

  return (
    <div className='flex flex-col gap-6 pb-2'>
      <p className='text-sm leading-relaxed text-gray-700'>
        {withWebAddressesAsLinks(pricingSchedule.introduction)}
      </p>
      <ContractData rows={pricingSchedule.contractData} />
      {pricingSchedule.supersedesNotice && (
        <p className='rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800'>
          {pricingSchedule.supersedesNotice}
        </p>
      )}
      <section className='flex flex-col gap-3'>
        <ClauseLead number={rates.number} text={rates.heading} />
        <TwoColumnTable
          labelTitle={rates.serviceColumnTitle}
          valueTitle={rates.priceColumnTitle}
          rows={rates.rows.map((rate) => ({
            label: rate.service,
            value: rate.display,
            isMuted: rate.price === null || rate.price === undefined,
          }))}
        />
        {rates.notes.map((note) => (
          <p key={note} className='text-sm text-gray-600'>
            {note}
          </p>
        ))}
      </section>
      {volumeDiscounts && (
        <section className='flex flex-col gap-3'>
          <ClauseLead
            number={volumeDiscounts.number}
            text={volumeDiscounts.heading}
          />
          <TwoColumnTable
            labelTitle={volumeDiscounts.turnoverColumnTitle}
            valueTitle={volumeDiscounts.discountColumnTitle}
            rows={volumeDiscounts.rows.map((discount) => ({
              label: discount.turnoverBand,
              value: discount.discount,
            }))}
          />
          <p className='text-sm text-gray-600'>{volumeDiscounts.note}</p>
        </section>
      )}
      <Forecast forecast={pricingSchedule.forecast} />
      <div className='flex flex-col gap-3'>
        {pricingSchedule.closingParagraphs.map((paragraph) => (
          <p key={paragraph} className='text-sm leading-relaxed text-gray-700'>
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  );
}

export function PricingScheduleModal({
  matchId,
  isOpen,
  setIsOpen,
  mode,
  onAccepted,
}: PricingScheduleModalProps) {
  const authHttp = useAuthHttp();
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const [loadedSchedule, setLoadedSchedule] =
    useState<WarehouseMatchPricingScheduleResponse | null>(null);
  const [refusedAcceptances, setRefusedAcceptances] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const matchIdOnScreen = useRef<string | null>(matchId);
  const pricingSchedule = loadedSchedule?.pricingSchedule ?? null;
  const isAwaitingYourAcceptance =
    loadedSchedule?.isAwaitingYourAcceptance ?? false;

  const showErrorHereInsteadOfTheGlobalDialog = (
    message: SetStateAction<string | null>
  ) => {
    setErrorResponseStatusCode(null);
    setResponseErrorMessages([]);
    setErrorMessage(message);
  };

  useEffect(() => {
    matchIdOnScreen.current = matchId;
    return () => {
      matchIdOnScreen.current = null;
    };
  }, [matchId]);

  useEffect(() => {
    if (!isOpen || !matchId) return;
    setLoadedSchedule(null);
    setErrorMessage(null);
  }, [matchId, isOpen]);

  useEffect(() => {
    if (!isOpen || !matchId) return undefined;

    let isStillThisMatch = true;
    setIsLoading(true);

    authHttp
      .get<WarehouseMatchPricingScheduleResponse>(
        GET_WAREHOUSE_MATCH_PRICING_SCHEDULE,
        { params: { matchId } }
      )
      .then(({ data }) => {
        if (!isStillThisMatch) return;
        setLoadedSchedule(data);
      })
      .catch((error) => {
        if (!isStillThisMatch) return;
        const reason =
          whatTheBackendSaid(error) ??
          'We could not load the Pricing Schedule. Please try again.';
        setLoadedSchedule(
          (scheduleOnScreen) =>
            scheduleOnScreen && {
              ...scheduleOnScreen,
              isAwaitingYourAcceptance: false,
            }
        );
        showErrorHereInsteadOfTheGlobalDialog((refusalOnScreen) =>
          refusalOnScreen ? `${refusalOnScreen} ${reason}` : reason
        );
      })
      .finally(() => {
        if (isStillThisMatch) setIsLoading(false);
      });

    return () => {
      isStillThisMatch = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, isOpen, mode, refusedAcceptances]);

  const closeUnlessAccepting = (value: boolean) => {
    if (isAccepting) return;
    setIsOpen(value);
  };

  const handleAccept = async () => {
    if (!pricingSchedule) return;

    const acceptedMatchId = matchId;
    setIsAccepting(true);
    setErrorMessage(null);
    try {
      const { data } = await authHttp.post<{ match: Match }>(
        ACCEPT_WAREHOUSE_MATCH,
        {
          match: { matchId: acceptedMatchId },
          pricingScheduleFingerprint: pricingSchedule.fingerprint,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (matchIdOnScreen.current !== acceptedMatchId) return;
      setIsOpen(false);
      onAccepted?.(data.match);
    } catch (error) {
      if (matchIdOnScreen.current !== acceptedMatchId) return;
      showErrorHereInsteadOfTheGlobalDialog(
        whatTheBackendSaid(error) ??
          'We could not accept the Pricing Schedule. Please try again.'
      );
      if (hasTheServerAnswered(error)) {
        setIsLoading(true);
        setRefusedAcceptances((refusals) => refusals + 1);
      }
    } finally {
      setIsAccepting(false);
    }
  };

  const acceptanceRecord = pricingSchedule
    ? acceptanceRecordOf(pricingSchedule.acceptance)
    : null;
  const acceptanceStatement =
    mode === 'accept' && isAwaitingYourAcceptance
      ? pricingSchedule?.acceptance.statement
      : null;
  const footerNote = acceptanceRecord ?? acceptanceStatement;
  const amendmentRecord =
    mode === 'view' && loadedSchedule?.amendedAt
      ? `Amended by FLOX on ${toDisplayDate(loadedSchedule.amendedAt)}`
      : null;

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={closeUnlessAccepting}
      title='Pricing Schedule'
      className='h-[90vh]'
      stableHeight
      isPrintable
    >
      <div className='flex min-h-0 w-full flex-1 flex-col gap-4 sm:w-[48rem] sm:max-w-full'>
        {pricingSchedule && (
          <p className='flex-none text-sm text-gray-500'>
            Agreement No. {pricingSchedule.agreementNumber}
          </p>
        )}
        <div className='min-h-0 flex-1 overflow-y-auto pr-1 print:overflow-visible'>
          {isLoading && !pricingSchedule && (
            <div className='flex flex-col items-center gap-3 py-16'>
              <FloxSpinner size='md' />
              <p className='text-sm text-gray-500'>
                Loading the Pricing Schedule...
              </p>
            </div>
          )}
          {errorMessage && !pricingSchedule && (
            <div className='py-16'>
              <ErrorAlert message={errorMessage} />
            </div>
          )}
          {pricingSchedule && (
            <PricingScheduleBody pricingSchedule={pricingSchedule} />
          )}
        </div>
        {errorMessage && pricingSchedule && (
          <ErrorAlert message={errorMessage} />
        )}
        <div className='flex flex-none flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center'>
          {(footerNote || amendmentRecord) && (
            <div className='flex flex-col gap-1 text-sm text-gray-600 sm:mr-auto'>
              {footerNote && <p>{footerNote}</p>}
              {amendmentRecord && <p>{amendmentRecord}</p>}
            </div>
          )}
          <div className='flex shrink-0 justify-end gap-3 sm:ml-auto print:hidden'>
            {mode === 'accept' ? (
              <>
                <Button
                  label='Cancel'
                  intent='secondary'
                  size='sm'
                  disabled={isAccepting}
                  onClick={() => closeUnlessAccepting(false)}
                />
                <Button
                  label={isAccepting ? 'Accepting...' : 'Accept'}
                  intent='primary'
                  size='sm'
                  disabled={
                    isLoading ||
                    isAccepting ||
                    !pricingSchedule ||
                    !isAwaitingYourAcceptance
                  }
                  onClick={handleAccept}
                />
              </>
            ) : (
              <>
                <Button
                  label='Print'
                  intent='secondary'
                  size='sm'
                  disabled={!pricingSchedule}
                  onClick={() => window.print()}
                />
                <Button
                  label='Close'
                  intent='secondary'
                  size='sm'
                  onClick={() => setIsOpen(false)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
